// src/screens/tabs/Menu/components/MenuFilters/FilterDropdown.tsx
import React, { useState } from "react";
import { View, TouchableOpacity, Text, Modal, StyleSheet } from "react-native";
import { Filter, X } from "lucide-react-native";
import { MenuFilterState } from "../screens/menu/FilterTypes";
import { FlickOColors } from "../styles/color";
import { getResponsiveSize } from "../styles/globalStyles";

interface FilterDropdownProps {
  filters: MenuFilterState;
  onApplyFilters: (filters: MenuFilterState) => void;
  onClearFilters: () => void;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filters,
  onApplyFilters,
  onClearFilters,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tempFilters, setTempFilters] = useState<MenuFilterState>(filters);

  const handleApply = () => {
    onApplyFilters(tempFilters);
    setIsVisible(false);
  };

  return (
    <>
      <View style={styles.container}>
        <TouchableOpacity
          style={styles.filterButton}
          onPress={() => setIsVisible(true)}
        >
          <Filter size={10} color={FlickOColors.primary} />
          <Text style={styles.filterButtonText}>Filters</Text>
        </TouchableOpacity>
      </View>
      <Modal
        visible={isVisible}
        animationType="slide"
        transparent
        onRequestClose={() => setIsVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Filters</Text>
              <TouchableOpacity onPress={() => setIsVisible(false)}>
                <X size={24} color={FlickOColors.text.primary} />
              </TouchableOpacity>
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.sectionTitle}>Sort By</Text>
              {[
                { label: "Price: Low to High", value: "price-low-high" },
                { label: "Price: High to Low", value: "price-high-low" },
                { label: "Rating: High to Low", value: "rating-high-low" },
              ].map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={[
                    styles.radioOption,
                    tempFilters.sortBy === option.value && styles.radioSelected,
                  ]}
                  onPress={() =>
                    setTempFilters({
                      ...tempFilters,
                      sortBy: option.value as MenuFilterState["sortBy"],
                    })
                  }
                >
                  <Text style={styles.radioText}>{option.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.filterSection}>
              <Text style={styles.sectionTitle}>Rating</Text>
              {[4, 3].map((rating) => (
                <TouchableOpacity
                  key={rating}
                  style={[
                    styles.radioOption,
                    tempFilters.rating === rating && styles.radioSelected,
                  ]}
                  onPress={() =>
                    setTempFilters({
                      ...tempFilters,
                      rating: rating,
                    })
                  }
                >
                  <Text style={styles.radioText}>{rating}+ Rating</Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={styles.clearButton}
                onPress={() => {
                  onClearFilters();
                  setIsVisible(false);
                }}
              >
                <Text style={styles.clearButtonText}>Clear All</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.applyButton}
                onPress={handleApply}
              >
                <Text style={styles.applyButtonText}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: getResponsiveSize(12),
    backgroundColor: FlickOColors.white,
  },
  filterButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: getResponsiveSize(16),
    paddingVertical: getResponsiveSize(12),
    borderRadius: getResponsiveSize(6),
    backgroundColor: FlickOColors.primary + "10",
  },
  filterButtonText: {
    marginLeft: getResponsiveSize(8),
    color: FlickOColors.primary,
    fontSize: getResponsiveSize(14),
    fontWeight: "500",
  },
  modalContainer: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    justifyContent: "flex-end",
  },
  modalContent: {
    backgroundColor: FlickOColors.white,
    borderTopLeftRadius: getResponsiveSize(20),
    borderTopRightRadius: getResponsiveSize(20),
    padding: getResponsiveSize(16),
    maxHeight: "80%",
  },
  modalHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: getResponsiveSize(16),
  },
  modalTitle: {
    fontSize: getResponsiveSize(18),
    fontWeight: "600",
    color: FlickOColors.text.primary,
  },
  filterSection: {
    marginBottom: getResponsiveSize(24),
  },
  sectionTitle: {
    fontSize: getResponsiveSize(16),
    fontWeight: "500",
    color: FlickOColors.text.primary,
    marginBottom: getResponsiveSize(12),
  },
  radioOption: {
    paddingVertical: getResponsiveSize(12),
    paddingHorizontal: getResponsiveSize(16),
    borderRadius: getResponsiveSize(8),
    marginBottom: getResponsiveSize(8),
    backgroundColor: FlickOColors.background.dark,
  },
  radioSelected: {
    backgroundColor: FlickOColors.primary + "20",
  },
  radioText: {
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.primary,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: getResponsiveSize(16),
  },
  clearButton: {
    flex: 1,
    paddingVertical: getResponsiveSize(12),
    marginRight: getResponsiveSize(8),
    borderRadius: getResponsiveSize(8),
    borderWidth: 1,
    borderColor: FlickOColors.primary,
  },
  clearButtonText: {
    textAlign: "center",
    color: FlickOColors.primary,
    fontSize: getResponsiveSize(14),
    fontWeight: "500",
  },
  applyButton: {
    flex: 1,
    paddingVertical: getResponsiveSize(12),
    marginLeft: getResponsiveSize(8),
    borderRadius: getResponsiveSize(8),
    backgroundColor: FlickOColors.primary,
  },
  applyButtonText: {
    textAlign: "center",
    color: FlickOColors.white,
    fontSize: getResponsiveSize(14),
    fontWeight: "500",
  },
});
